import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, Loader2, Receipt, TrendingUp, Wallet } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtCAD, fmtDate, fmtInt } from "@/lib/billing";

export default function BillingDashboardCards() {
  const [summary, setSummary] = useState(null);
  const [spend, setSpend] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [s, p] = await Promise.all([api.get("/admin/billing/summary"), api.get("/admin/billing/spend")]);
        setSummary(s.data);
        setSpend(p.data);
      } catch (e) {
        setErr(formatApiError(e));
        toast.error("Failed to load billing summary", { description: formatApiError(e) });
      }
    })();
  }, []);

  return (
    <section className="rounded-lg border border-slate-200 bg-white" data-testid="billing-dashboard">
      <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
        <CreditCard className="h-4 w-4 text-slate-400" />
        <h2 className="text-base md:text-lg font-semibold text-slate-900">Billing</h2>
        {summary ? (
          <span className={"rounded-full border px-2 py-0.5 text-[11px] font-medium " + (summary.enabled ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200")} data-testid="billing-dashboard-enabled">
            {summary.enabled ? `Live since ${fmtDate(summary.launch_date)}` : "Billing off"}
          </span>
        ) : null}
        <Link to="/settings" className="ml-auto text-xs font-semibold underline text-slate-600" data-testid="billing-dashboard-settings-link">Billing settings</Link>
      </div>
      {err ? (
        <p className="p-4 text-sm text-rose-700">{err}</p>
      ) : !summary || !spend ? (
        <div className="p-6 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
            <Stat label="MRR" value={fmtCAD(summary.mrr_cad)} sub={`${fmtCAD(summary.plan_price_cad)}/member`} testid="billing-stat-mrr" />
            <Stat label="Active" value={fmtInt(summary.counts.active)} sub={`${summary.counts.cancelling} cancelling`} testid="billing-stat-active" />
            <Stat label="Payment issues" value={fmtInt(summary.counts.past_due + summary.counts.read_only)} sub={`${summary.counts.past_due} past due · ${summary.counts.read_only} read-only`} tone={summary.counts.past_due + summary.counts.read_only > 0 ? "warn" : ""} testid="billing-stat-issues" />
            <Stat label="Complimentary" value={fmtInt(summary.counts.complimentary)} testid="billing-stat-complimentary" />
            <Stat label="Not activated" value={fmtInt(summary.counts.inactive)} testid="billing-stat-inactive" />
            <Stat label="Ended" value={fmtInt(summary.counts.ended || 0)} sub="limited access" testid="billing-stat-ended" />
            <Stat label="Top-ups this month" value={fmtCAD(summary.topups_month_cad)} sub={`${summary.topups_month_count} pack(s)`} testid="billing-stat-topups" />
            <Stat label="Revenue this month" value={fmtCAD(summary.revenue_month_cad)} testid="billing-stat-revenue" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <Panel icon={Receipt} title="Renewals · next 7 days" testid="billing-renewals-panel">
              {summary.renewals_next_7_days.length ? (
                <ul className="divide-y divide-slate-100 text-sm">
                  {summary.renewals_next_7_days.map((r) => (
                    <li key={r.email + r.at} className="py-1.5 flex items-center justify-between gap-2">
                      <span className="truncate">{r.name || r.email}</span>
                      <span className="text-xs text-slate-500 whitespace-nowrap">{fmtDate(r.at)} · {fmtCAD(r.amount_cad)}</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-xs text-slate-500">No card renewals due this week.</p>}
              <p className="text-[11px] text-slate-400 mt-2">Last run: {summary.renewals_last_run?.at ? new Date(summary.renewals_last_run.at).toLocaleString() : "never"} · webhook: {summary.webhook_last_event_at ? new Date(summary.webhook_last_event_at).toLocaleString() : "no events yet"}</p>
            </Panel>

            <Panel icon={TrendingUp} title="GST / HST tracker" testid="billing-gst-panel">
              <div className="flex items-center justify-between text-xs text-slate-600">
                <span>Taxable sales · trailing 12 months</span>
                <span className="font-medium text-slate-900 tabular-nums">{fmtCAD(summary.taxable_sales_12m_cad)} / {fmtCAD(summary.gst_threshold_cad)}</span>
              </div>
              <div className="mt-2 h-2 rounded-full bg-slate-200 overflow-hidden">
                <div className={summary.gst_active ? "h-full bg-amber-500" : "h-full bg-emerald-500"} style={{ width: `${Math.min(100, Math.round((summary.taxable_sales_12m_cad / Math.max(summary.gst_threshold_cad, 1)) * 100))}%` }} />
              </div>
              <p className={"text-xs mt-2 " + (summary.gst_active ? "text-amber-700 font-medium" : "text-slate-500")} data-testid="billing-gst-panel-status">
                {summary.gst_active ? "Charging GST/HST" : "Not charging GST/HST"} — {summary.gst_reason}
              </p>
            </Panel>

            <Panel icon={Wallet} title="Acquisition spend (Apify + Firecrawl)" testid="billing-spend-panel">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="py-1"></th><th className="py-1 text-right">This month</th><th className="py-1 text-right">All time</th>
                  </tr>
                </thead>
                <tbody className="text-slate-700">
                  <Row label="Apify" m={`US$${spend.month.apify_usd}`} a={`US$${spend.all.apify_usd}`} />
                  <Row label="Firecrawl" m={`US$${spend.month.firecrawl_usd}`} a={`US$${spend.all.firecrawl_usd}`} />
                  <Row label="Total (CAD)" m={fmtCAD(spend.month.total_cad)} a={fmtCAD(spend.all.total_cad)} bold />
                  <Row label="Per realtor" m={fmtCAD(spend.month.per_realtor_cad)} a={fmtCAD(spend.all.per_realtor_cad)} />
                  <Row label="Realtors added" m={fmtInt(spend.month.agents_added)} a={fmtInt(spend.all.agents_added)} />
                </tbody>
              </table>
              <p className="text-[11px] text-slate-400 mt-2">+ fixed subscriptions ≈ {fmtCAD(spend.fixed_monthly_cad)}/month · rate {spend.usd_cad_rate}</p>
            </Panel>
          </div>
        </div>
      )}
    </section>
  );
}

function Stat({ label, value, sub, tone, testid }) {
  return (
    <div className={"rounded-lg border p-3 " + (tone === "warn" ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white")} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-medium text-slate-400">{label}</div>
      <div className="text-xl font-bold text-slate-900 mt-1 tabular-nums">{value}</div>
      {sub ? <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div> : null}
    </div>
  );
}

function Panel({ icon: Icon, title, children, testid }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900 mb-2"><Icon className="h-4 w-4 text-slate-400" /> {title}</div>
      {children}
    </div>
  );
}

function Row({ label, m, a, bold }) {
  return (
    <tr className={bold ? "font-semibold text-slate-900" : ""}>
      <td className="py-0.5">{label}</td>
      <td className="py-0.5 text-right tabular-nums">{m}</td>
      <td className="py-0.5 text-right tabular-nums">{a}</td>
    </tr>
  );
}
